/**
 * THE AGREEMENT CARD on a job: send the shop's agreement for signature,
 * see where it stands, countersign it, open the executed PDF.
 *
 * Anthony, 2026-10-06: the shop checks the quote was approved and sends the
 * document over. So the card shows what says yes (an authorization, a
 * confirmed booking, a "yes" text) as a hint beside the button and leaves
 * the decision to the person reading it. Nothing goes out on its own.
 *
 * Self-contained: it asks the server (server/routes/esign.ts) for
 * everything, so a shop with signing switched off gets no card at all.
 *
 * TWO DOORS IN, ONE CARD. JEV for NYIA, 2026-10-15 (D20): the call desk
 * sends to a caller with no job (src/pages/calldesk.ts). Same card, a
 * different target, so the countersign, the withdraw and the PDF are the
 * ones the job card already has rather than a copy of them.
 */

import { api, db } from "../data/client.ts";
import { esc, money, since } from "../ui/dom.ts";

type Spec = { label: string; default?: string; hint?: string; choices?: Record<string, { label: string }>; when?: string };
type Env = { id: string; job_id?: string | null; title: string; status: string; status_words: string; signer_name: string; signer_email: string | null;
  signer_phone10: string | null; sent_at: string; sent_by_name: string | null; first_viewed_at: string | null;
  client_signed_at: string | null; countersigned_at: string | null; countersigned_name: string | null; completed_at: string | null;
  declined_at: string | null; decline_reason: string | null; voided_at: string | null; doc_sha256: string; signature: string | null;
  signed_name: string | null; pdf_ready: boolean };
type State = { on: boolean; error?: string;
  template: { title: string; version: number; fields: Record<string, Spec>; countersign_label: string | null } | null;
  signer: { name: string; email: string; phone: string; abroad?: string }; suggested: { assignment: string }; quote_total: number;
  approval: { authorized: { amount: number; at: string; method: string } | null; confirmed_at: string | null;
    replied_yes: { at: string; body: string } | null };
  envelopes: Env[]; may_countersign: boolean; replace_ok?: boolean; send_by?: boolean };

/* SEND BY (call desk, Anthony 2026-10-18: "send the agreement through email
   or email and text"). A select, not radio buttons: on a phone it opens the
   phone's own picker and cannot run off the screen. */
const BY_WORDS: Record<string, string> = { both: "by email and text", email: "by email", sms: "by text" };
function sendByHtml(st: State): string {
  if (!st.send_by) return "";
  const pick = st.signer.abroad || !st.signer.phone ? "email" : !st.signer.email ? "sms" : "both";
  const opt = (v: string, label: string) => `<option value="${v}"${v === pick ? " selected" : ""}>${label}</option>`;
  return `<label class="fine" style="display:block">Send by
      <select id="esign-by" style="display:block;width:100%;max-width:100%">${opt("both", "Text and email")}${opt("email", "Email only")}${opt("sms", "Text only")}</select></label>
    ${st.signer.abroad ? `<div class="fine">Their number is ${esc(st.signer.abroad)}. Texts only go to +1 numbers, so send it by email.</div>` : ""}`;
}

export async function authed(path: string, body?: unknown): Promise<Response> {
  const sb = await db();
  const { data: { session } } = await sb.auth.getSession();
  return fetch(api(path), {
    method: body ? "POST" : "GET",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token ?? ""}` },
    body: body ? JSON.stringify(body) : undefined,
  });
}

const phone = (p: string | null) => p && p.length === 10 ? `(${p.slice(0, 3)}) ${p.slice(3, 6)}-${p.slice(6)}` : "";

/** What the card is about: a job, or (call desk only) a caller. */
type Target =
  | { kind: "job"; jobId: string }
  | { kind: "caller"; phone10: string; name: string };

export async function mountEsign(jobId: string): Promise<void> {
  const host = document.getElementById("esign-card");
  if (!host) return;
  await mountOn(host, { kind: "job", jobId });
}

/** The card for one caller, on a call desk. Says why when there is no card. */
export async function mountEsignForCaller(host: HTMLElement, phone10: string, name: string): Promise<void> {
  host.hidden = false;
  host.innerHTML = `<div class="fine">Opening the agreement&hellip;</div>`;
  const shown = await mountOn(host, { kind: "caller", phone10, name });
  if (!shown) host.innerHTML = `<div class="fine">Agreements are not switched on for this shop, or this login cannot send them.</div>`;
}

async function mountOn(host: HTMLElement, target: Target): Promise<boolean> {
  let st: State;
  try {
    const r = await authed(target.kind === "job"
      ? `/api/esign/staff/job?job_id=${encodeURIComponent(target.jobId)}`
      : `/api/esign/staff/caller?phone=${encodeURIComponent(target.phone10)}&name=${encodeURIComponent(target.name)}`);
    if (!r.ok) return false;                 // not a desk role, or not this shop's job: no card
    st = await r.json() as State;
  } catch { return false; }
  if (!st.on) return false;
  host.hidden = false;
  draw(host, st, target);
  /* Arrived from Send agreement on a lead (leads.ts): land on the card. */
  try {
    if (target.kind === "job" && sessionStorage.getItem("esign:focus") === target.jobId) {
      sessionStorage.removeItem("esign:focus");
      host.scrollIntoView({ block: "start", behavior: "smooth" });
      host.classList.add("flash");
    }
  } catch { /* storage refused: the card is still above the tabs */ }
  return true;
}

function approvalLine(a: State["approval"]): string {
  const bits: string[] = [];
  if (a.authorized) bits.push(`approved ${money(a.authorized.amount)} ${since(a.authorized.at)}`);
  if (a.replied_yes) bits.push(`texted "${esc(a.replied_yes.body)}" ${since(a.replied_yes.at)}`);
  if (a.confirmed_at) bits.push(`confirmed the booking ${since(a.confirmed_at)}`);
  return bits.length
    ? `<div class="fine ok">The client ${bits.join(", and ")}.</div>`
    : `<div class="fine">No approval on record yet. Check they said yes before you send.</div>`;
}

function draw(host: HTMLElement, st: State, target: Target): void {
  const live = st.envelopes.find((e) => ["sent", "viewed", "client_signed"].includes(e.status));
  const past = st.envelopes.filter((e) => e !== live);
  const t = st.template;

  /* NYIA (replace_old): an unsigned agreement can be cancelled and the new
     one sent in one step, so the form stays under it. */
  const replacing = !!live && !!st.replace_ok && (live.status === "sent" || live.status === "viewed");
  const form = (!live || replacing) && t ? `
    ${target.kind === "job" ? approvalLine(st.approval) : ""}
    <div class="esign-form">
      ${target.kind === "caller" ? `<label class="fine">Name <input type="text" id="esign-name" value="${esc(st.signer.name)}"></label>` : ""}
      ${Object.entries(t.fields).map(([k, f]) => `
        <label class="fine"${f.when ? ` data-eswhen="${esc(f.when)}"` : ""}>${esc(f.label)}${f.hint ? ` <span class="quiet">(${esc(f.hint)})</span>` : ""}
          ${f.choices ? `<select data-esf="${esc(k)}">${Object.entries(f.choices).map(([v, c]) =>
            `<option value="${esc(v)}"${v === f.default ? " selected" : ""}>${esc(c.label)}</option>`).join("")}</select>`
          : `<input type="text" data-esf="${esc(k)}" value="${esc(f.default ?? (k === "flat_fee" && st.quote_total ? st.quote_total.toFixed(2) : ""))}">`}</label>`).join("")}
      <label class="fine">Email <input type="text" id="esign-email" value="${esc(st.signer.email)}"></label>
      <label class="fine">Mobile <input type="text" id="esign-phone" value="${esc(phone(st.signer.phone))}"></label>
      ${sendByHtml(st)}
      <div class="row"><button class="btn" id="esign-send">${replacing ? "Cancel the old agreement and send this one" : `Send ${esc(t.title)} to ${esc(st.signer.name || "the client")}`}</button></div>
      <div class="fine">They get a link <span id="esign-by-words">${BY_WORDS[st.send_by ? (st.signer.abroad || !st.signer.phone ? "email" : !st.signer.email ? "sms" : "both") : "both"]}</span>, agree to sign electronically, confirm a code, and sign. You countersign here after.</div>
    </div>` : !t ? `<div class="fine">No agreement on file for this shop yet.</div>` : "";

  const liveHtml = live ? `
    <div class="authrec">
      <div class="authrec-h"><b>${esc(live.title)}</b> <span class="pill">${esc(live.status_words)}</span></div>
      <div class="authrec-w">To ${esc(live.signer_name)}${live.signer_email ? ` · ${esc(live.signer_email)}` : ""}${live.signer_phone10 ? ` · ${esc(phone(live.signer_phone10))}` : ""}
        <span class="when">sent ${esc(since(live.sent_at))}${live.sent_by_name ? ` by ${esc(live.sent_by_name)}` : ""}${live.first_viewed_at ? `, opened ${esc(since(live.first_viewed_at))}` : ""}</span></div>
      ${live.client_signed_at ? `<div class="authrec-w">Signed by <b>${esc(live.signed_name ?? live.signer_name)}</b> <span class="when">${esc(since(live.client_signed_at))}</span></div>
        ${live.signature ? `<img class="authrec-sig" alt="Signature of ${esc(live.signer_name)}" src="${esc(live.signature)}">` : ""}` : ""}
      ${live.status === "client_signed" && st.may_countersign ? `
        <div class="esign-form">
          <label class="fine">${esc(t?.countersign_label ?? "Countersign")}: type your name <input type="text" id="esign-cs-name"></label>
          <canvas class="esign-pad" id="esign-pad"></canvas>
          <label class="fine"><input type="checkbox" id="esign-cs-ok"> I countersign this agreement for the shop, and intend this as my signature.</label>
          <div class="row"><button class="btn" id="esign-cs">Countersign and send the executed copy</button></div>
        </div>` : live.status === "client_signed" ? `<div class="fine">Waiting on the countersignature.</div>` : ""}
      <div class="row"><a class="btn ghost" href="#" data-esign-pdf="${esc(live.id)}">Open PDF</a>
        <button class="btn ghost" data-esign-void="${esc(live.id)}">Withdraw</button></div>
    </div>` : "";

  const pastHtml = past.length ? past.map((e) => `
    <div class="authrec ${e.status === "completed" ? "" : "dec"}">
      <div class="authrec-h"><b>${esc(e.title)}</b> <span class="pill">${esc(e.status_words)}</span></div>
      <div class="authrec-w">${esc(e.signer_name)} <span class="when">${esc(since(e.completed_at ?? e.declined_at ?? e.voided_at ?? e.sent_at))}</span>
        ${e.decline_reason ? `<div class="fine">They said: ${esc(e.decline_reason)}</div>` : ""}</div>
      ${e.status === "completed" ? `<a class="btn ghost" href="#" data-esign-pdf="${esc(e.id)}">Open executed PDF</a>` : ""}
    </div>`).join("") : "";

  /* PAID UP FRONT (job.ts puts the payment card straight under this one
     on a case file). Once they have signed, say what comes next. */
  const signedEnv = st.envelopes.find((e) => e.status === "client_signed" || e.status === "completed");
  /* From the Calls screen there is no payment card on the page: the case
     the agreement was filed against has it, right under the agreement. */
  const caseOf = st.envelopes.find((e) => e.job_id)?.job_id ?? null;
  const next = target.kind === "caller"
    ? caseOf ? `<div class="row"><a class="btn ${signedEnv ? "primary" : "ghost"}" href="#/job/${esc(caseOf)}">${
        signedEnv ? "Signed. Take the payment on their case" : "Open their case"}</a></div>` : ""
    : signedEnv && document.getElementById("prepay-card")
    ? `<div class="fine ok">Signed. Next, take the payment just below.</div>` : "";
  host.innerHTML = `<div class="card-head">Agreement for signature</div>${liveHtml}${form}${pastHtml}${next}<div class="fine" id="esign-msg" role="status"></div>`;
  const msg = (s: string) => { const m = document.getElementById("esign-msg"); if (m) m.textContent = s; };
  const reload = () => void mountOn(host, target);
  /* A box that belongs to one choice shows only while that choice is picked,
     and is not sent otherwise, so a flat fee never carries an hourly rate. */
  const pickedNow = (w: string) => { const [k, v] = w.split(":"); return host.querySelector<HTMLSelectElement>(`select[data-esf="${k}"]`)?.value === v; };
  const showPicked = () => host.querySelectorAll<HTMLElement>("[data-eswhen]").forEach((l) => { l.hidden = !pickedNow(l.dataset["eswhen"]!); });
  host.querySelectorAll<HTMLSelectElement>("select[data-esf]").forEach((sel) => sel.addEventListener("change", showPicked));
  showPicked();
  const bySel = document.getElementById("esign-by") as HTMLSelectElement | null;
  bySel?.addEventListener("change", () => { const w = document.getElementById("esign-by-words"); if (w) w.textContent = BY_WORDS[bySel.value] ?? ""; });

  document.getElementById("esign-send")?.addEventListener("click", async (ev) => {
    if (replacing && !confirm("Cancel the agreement already out to them and send this one? The old link stops working.")) return;
    const b = ev.currentTarget as HTMLButtonElement; b.disabled = true; msg("Sending…");
    const fields: Record<string, string> = {};
    host.querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-esf]").forEach((i) => {
      const w = i.closest<HTMLElement>("[data-eswhen]")?.dataset["eswhen"];
      if (!w || pickedNow(w)) fields[i.dataset["esf"]!] = i.value;
    });
    const email = (document.getElementById("esign-email") as HTMLInputElement).value;
    const phoneIn = (document.getElementById("esign-phone") as HTMLInputElement).value;
    const by = bySel ? { send_by: bySel.value } : {};
    const r = target.kind === "job"
      ? await authed("/api/esign/staff/send", { job_id: target.jobId, fields, email, phone: phoneIn, replace: replacing, ...by })
      : await authed("/api/esign/staff/send-caller", { fields, email, phone: phoneIn, replace: replacing, key: target.phone10, ...by,
        name: (document.getElementById("esign-name") as HTMLInputElement | null)?.value ?? "" });
    const out = await r.json().catch(() => ({})) as { ok?: boolean; error?: string; delivered?: { email: boolean; sms: boolean; why: string[] } };
    if (!r.ok || !out.ok) { msg(out.error ?? "That did not go."); b.disabled = false; return; }
    const d = out.delivered!;
    msg(`Sent${d.email ? " by email" : ""}${d.email && d.sms ? " and" : ""}${d.sms ? " by text" : ""}.${d.why.length ? ` (${d.why.join("; ")})` : ""}`);
    setTimeout(reload, 900);
  });

  host.querySelectorAll<HTMLElement>("[data-esign-void]").forEach((b) => b.addEventListener("click", async () => {
    const reason = prompt("Withdraw this agreement? The link stops working. Reason (optional):");
    if (reason === null) return;
    const r = await authed("/api/esign/staff/void", { envelope_id: b.dataset["esignVoid"], reason });
    const out = await r.json().catch(() => ({})) as { error?: string };
    if (!r.ok) { msg(out.error ?? "Could not withdraw it."); return; }
    reload();
  }));

  host.querySelectorAll<HTMLElement>("[data-esign-pdf]").forEach((a) => a.addEventListener("click", async (ev) => {
    ev.preventDefault();
    const win = window.open("", "_blank");
    const r = await authed(`/api/esign/staff/pdf?envelope_id=${encodeURIComponent(a.dataset["esignPdf"]!)}`);
    if (!r.ok) { win?.close(); msg("Could not open the PDF."); return; }
    const url = URL.createObjectURL(await r.blob());
    if (win) win.location.href = url; else location.href = url;
  }));

  const pad = document.getElementById("esign-pad") as HTMLCanvasElement | null;
  let drew = false;
  if (pad) {
    const ctx = pad.getContext("2d")!;
    requestAnimationFrame(() => {
      const r = pad.getBoundingClientRect(), d = window.devicePixelRatio || 1;
      pad.width = r.width * d; pad.height = r.height * d; ctx.scale(d, d);
      ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.strokeStyle = "#13294b";
    });
    let down = false;
    const at = (e: PointerEvent): [number, number] => { const r = pad.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
    pad.addEventListener("pointerdown", (e) => { down = true; pad.setPointerCapture(e.pointerId); ctx.beginPath(); ctx.moveTo(...at(e)); });
    pad.addEventListener("pointermove", (e) => { if (!down) return; ctx.lineTo(...at(e)); ctx.stroke(); drew = true; });
    pad.addEventListener("pointerup", () => { down = false; });
  }
  document.getElementById("esign-cs")?.addEventListener("click", async (ev) => {
    const b = ev.currentTarget as HTMLButtonElement;
    const name = (document.getElementById("esign-cs-name") as HTMLInputElement).value.trim();
    const ok = (document.getElementById("esign-cs-ok") as HTMLInputElement).checked;
    if (!name || !ok) { msg("Type your name and tick the box."); return; }
    b.disabled = true; msg("Countersigning and building the executed copy…");
    const r = await authed("/api/esign/staff/countersign", { envelope_id: live!.id, signed_name: name,
      signature: pad && drew ? pad.toDataURL("image/png") : "", agree: true });
    const out = await r.json().catch(() => ({})) as { ok?: boolean; error?: string; warning?: string };
    if (!r.ok || !out.ok) { msg(out.error ?? out.warning ?? "That did not go."); b.disabled = false; return; }
    msg("Done. The executed copy has been emailed to the client and to you.");
    setTimeout(reload, 900);
  });
}
